import { Link } from '@tanstack/react-router';
import { T } from 'gt-react';
import { Button } from '@/components/ui/button';
import type { LabelingCounts } from '@erp/rpc';
import type { ReviewView } from '../849-review/reviewView';

/**
 * The record's depth — what a person COULD label, and what was labelled today.
 *
 * The review queue's own count lives on the review page (#7656): the queue is
 * worked there, and a number here that nobody on this page can drain reads as
 * someone else's backlog.
 */
export function LabelingQueueStats({ stats }: { stats: LabelingCounts }) {
  return (
    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
      <span>
        <strong className="text-foreground">{stats.unlabeledEmails}</strong>{' '}
        <T context="labeling queue count">emails to label</T>
      </span>
      <span>
        <strong className="text-foreground">{stats.labeledToday}</strong>{' '}
        <T context="labeling queue count">labeled today</T>
      </span>
    </div>
  );
}

/**
 * The review queue's depth, first on the review page. The counts are deliberately
 * prominent: a queue with a visible bottom gets drained, an apparently infinite
 * one gets abandoned.
 */
export function ReviewQueueStats({ stats }: { stats: LabelingCounts }) {
  return (
    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
      <span>
        <strong className="text-foreground">{stats.reviewQueue}</strong>{' '}
        <T context="labeling queue count">need a decision</T>
      </span>
      <span>
        <strong className="text-foreground">{stats.labeledToday}</strong>{' '}
        <T context="labeling queue count">labeled today</T>
      </span>
    </div>
  );
}

/**
 * Review queue vs Claim aging, on the review page.
 *
 * Browse and Matches used to be modes beside these. Browse is now the record's own
 * page; Matches is gone (#7656): the engine matches rows within the claims a
 * reviewer links, and a row it could not place is re-pointed on the claim's
 * record page.
 */
export function ReviewViewToggle({
  view,
  onViewChange,
}: {
  view: ReviewView;
  onViewChange: (view: ReviewView) => void;
}) {
  return (
    <div className="flex gap-1">
      <Button
        size="sm"
        variant={view === 'queue' ? 'default' : 'outline'}
        aria-pressed={view === 'queue'}
        onClick={() => onViewChange('queue')}
      >
        <T context="labeling mode toggle">Review queue</T>
      </Button>
      <Button
        size="sm"
        variant={view === 'aging' ? 'default' : 'outline'}
        aria-pressed={view === 'aging'}
        onClick={() => onViewChange('aging')}
      >
        <T context="labeling mode toggle: per-supplier claim aging view">
          Claim aging
        </T>
      </Button>
    </div>
  );
}

/**
 * The record page's header actions. The label-only Undo that used to sit here
 * (and on the "Labeled" toast) is gone (#7652): a session answer can move money,
 * and taking back only its label left the correction standing. Answers are undone
 * from the claim's record page, where undo reverses everything they did.
 *
 * `canReview` adds the way to the review page for people who can work it.
 */
export function LabelingActions({ canReview }: { canReview: boolean }) {
  return (
    <div className="flex items-center gap-2">
      {canReview ? (
        <Button variant="outline" asChild>
          <Link to="/ship-and-debit/849-review">
            <T context="labeling header action">Open Review Queue</T>
          </Link>
        </Button>
      ) : null}
      <Button variant="outline" asChild>
        <Link to="/ship-and-debit/labeling/labeled">
          <T context="labeling header action">View labeled</T>
        </Link>
      </Button>
    </div>
  );
}

/** The review page's header actions: the record, one click away. */
export function ReviewActions() {
  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" asChild>
        <Link to="/ship-and-debit/labeling">
          <T context="849 review header action: open the full mailbox record">
            Browse All Emails
          </T>
        </Link>
      </Button>
      <Button variant="outline" asChild>
        <Link to="/ship-and-debit/labeling/labeled">
          <T context="labeling header action">View labeled</T>
        </Link>
      </Button>
    </div>
  );
}
